import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';

const Reviews = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = translations[language];
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(null);
  const observe = useScrollAnimation();

  const reviews = [
    {
      name: t.review1Name,
      text: t.review1Text,
      rating: 5,
      initials: 'M',
      bgColor: 'bg-primary text-white',
    },
    {
      name: t.review2Name,
      text: t.review2Text,
      rating: 5,
      initials: 'MT',
      bgColor: 'bg-amber-600 text-white',
    },
    {
      name: t.review3Name,
      text: t.review3Text,
      rating: 5,
      initials: 'AO',
      bgColor: 'bg-indigo-600 text-white',
    },
    {
      name: t.review4Name,
      text: t.review4Text,
      rating: 5,
      initials: 'MA',
      bgColor: 'bg-emerald-600 text-white',
    },
    {
      name: t.review5Name,
      text: t.review5Text,
      rating: 5,
      initials: 'RM',
      bgColor: 'bg-purple-600 text-white',
    },
  ];

  // Auto-slide every 4 seconds unless hovered
  useEffect(() => {
    // Keep react-snap on the deterministic initial slide so its saved HTML
    // matches the browser's first render during hydration.
    if (isPaused || navigator.userAgent === 'ReactSnap') return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % reviews.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isPaused, reviews.length]);

  const nextReview = () => {
    setActiveIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setActiveIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) return;

    const deltaX = event.changedTouches[0].clientX - touchStartX.current;
    const swipeThreshold = 50;

    if (Math.abs(deltaX) >= swipeThreshold) {
      const shouldShowNext = isAr ? deltaX > 0 : deltaX < 0;
      shouldShowNext ? nextReview() : prevReview();
    }

    touchStartX.current = null;
    setIsPaused(false);
  };

  const handleTouchCancel = () => {
    touchStartX.current = null;
    setIsPaused(false);
  };

  // Compute indices for 3 visible cards starting at activeIndex
  const visibleIndices = [
    activeIndex % reviews.length,
    (activeIndex + 1) % reviews.length,
    (activeIndex + 2) % reviews.length,
  ];

  return (
    <section 
      className="py-20 bg-white border-b border-edge-light"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-4">
          <span
            ref={observe}
            className="animate-fade-up inline-flex items-center justify-center px-6 py-2 rounded-full bg-badge text-primary-dark text-sm sm:text-base font-bold font-almarai mb-4"
          >
            {t.reviewsTitle}
          </span>
        </div>

        <h2
          ref={observe}
          className="animate-fade-up text-center font-bold font-ibm mb-4 text-heading"
          style={{ fontSize: 'clamp(24px, 5vw, 36px)', transitionDelay: '0.1s' }}
        >
          {t.reviewsSubtitle}
        </h2>

        {t.reviewsDescription ? (
          <p
            ref={observe}
            className="animate-fade-up text-center font-almarai mb-12 max-w-2xl mx-auto text-body-muted"
            style={{ fontSize: '16px', transitionDelay: '0.2s' }}
          >
            {t.reviewsDescription}
          </p>
        ) : (
          <div className="mb-12" />
        )}

        {/* Reviews Carousel Grid */}
        <div
          ref={observe}
          className="stagger-children grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 items-stretch touch-pan-y"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
        >
          {visibleIndices.map((idx, pos) => {
            const review = reviews[idx];
            const isSelected = idx === activeIndex;

            return (
              <div
                key={`${idx}-${pos}`}
                onClick={() => setActiveIndex(idx)}
                className={`stagger-item p-6 rounded-2xl bg-white transition-all duration-300 flex-col h-[240px] cursor-pointer relative overflow-hidden ${
                  pos > 0 ? 'hidden md:flex' : 'flex'
                } ${
                  isSelected
                    ? 'border-2 border-primary shadow-lg'
                    : 'border border-edge-card shadow-card-soft hover:border-primary/40'
                }`}
                style={{ transitionDelay: `${pos * 0.1}s` }}
              >
                <div className="flex items-center gap-3 mb-3 flex-shrink-0">
                  {/* Clean Avatar Circle */}
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-base font-ibm shadow-sm flex-shrink-0 ${review.bgColor}`}>
                    {review.initials}
                  </div>
                  <div className="flex flex-col">
                    <p className="font-bold font-almarai text-heading text-base">
                      {review.name}
                    </p>
                    <div className="flex gap-1 mt-0.5">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} size={14} className="fill-warning text-warning" />
                      ))}
                    </div>
                  </div>
                </div>

                <p className="font-almarai leading-relaxed text-body text-sm flex-grow">
                  {review.text}
                </p>
              </div>
            );
          })}
        </div>

        {/* Carousel Navigation & Indicators */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="flex items-center gap-0 sm:gap-3">
            <button
              onClick={prevReview}
              aria-label={isAr ? 'التقييم السابق' : 'Previous review'}
              className="w-10 h-10 rounded-full border border-edge flex items-center justify-center transition-colors hover:bg-surface-hover hover:border-primary text-heading"
            >
              {isAr ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
            
            {/* Dots */}
            <div className="flex items-center gap-4 max-[349px]:gap-0 px-0 sm:px-2">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  aria-label={"Go to slide " + (i + 1)}
                  className="h-8 w-8 rounded-full inline-flex items-center justify-center"
                >
                  <span
                    className={"h-2.5 rounded-full transition-all duration-300 " + (
                      activeIndex === i ? "w-6 bg-primary" : "w-2.5 bg-gray-200 hover:bg-gray-300"
                    )}
                  />
                </button>
              ))}
            </div>

            <button
              onClick={nextReview}
              aria-label={isAr ? 'التقييم التالي' : 'Next review'}
              className="w-10 h-10 rounded-full border border-edge flex items-center justify-center transition-colors hover:bg-surface-hover hover:border-primary text-heading"
            >
              {isAr ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;